import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Target, Heart, Star, Quote, User, Calendar, ExternalLink, Award, Video, Shield, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiagnosticPage = () => {
  const navigate = useNavigate();
  const { hasTier } = useAuth();
  const [data, setData] = useState({ sections: [], questions: [] });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Check for existing results first
      const resultsRes = await axios.get(`${API_URL}/api/diagnostic/results`);
      if (resultsRes.data) {
        setResults(resultsRes.data);
        setShowResults(true);
      }
      
      // Get questions and sections
      const questionsRes = await axios.get(`${API_URL}/api/diagnostic/questions`);
      setData(questionsRes.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch diagnostic data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    const currentQ = data.questions[currentQuestionIndex];
    if (currentQ && currentQ.required && !answers[currentQ.id]) {
      toast.error('Please answer this question before continuing');
      return;
    }
    if (currentQuestionIndex < data.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > -1) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    const requiredQuestions = data.questions.filter(q => q.required);
    const unanswered = requiredQuestions.filter(q => !answers[q.id]);
    
    if (unanswered.length > 0) {
      toast.error(`Please answer all required questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/diagnostic/submit`, { answers });
      setResults(response.data);
      setShowResults(true);
      toast.success('Your diagnostic is complete!');
    } catch (error) {
      toast.error('Failed to submit diagnostic');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = async () => {
    setAnswers({});
    setCurrentQuestionIndex(-1);
    setShowResults(false);
    setResults(null);
  };

  const getCurrentSection = () => {
    if (currentQuestionIndex < 0) return null;
    const currentQ = data.questions[currentQuestionIndex];
    return data.sections.find(s => s.id === currentQ?.section);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Show Results (Client View - No Scores)
  if (showResults && results) {
    const clientView = results.client_view || results;
    const coachMatch = results.coach_match;
    const matchedCoach = coachMatch?.coach;
    
    const handleBookChemistry = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/sessions/book/chemistry`);
        if (response.data.calendly_link) {
          window.open(response.data.calendly_link, '_blank');
          toast.success('Opening scheduling page...');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to book chemistry session');
      }
    };

    const handleBookSession = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/sessions/book/matched-coach`);
        if (response.data.calendly_link) {
          window.open(response.data.calendly_link, '_blank');
          toast.success('Opening scheduling page...');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to book session');
      }
    };
    
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="diagnostic-results">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-forest to-terracotta flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest mb-3">
              Your Inner Edge™️ Insights
            </h1>
            <p className="text-gray-600 text-lg">
              Here&apos;s what we discovered about your leadership journey
            </p>
          </div>

          {/* Matched Coach Card - NEW */}
          {matchedCoach && (
            <Card className="card-base mb-8 border-2 border-terracotta/30 bg-gradient-to-br from-terracotta/5 to-forest/5">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-terracotta" />
                  <CardTitle className="font-heading text-xl text-forest">
                    Your Matched Coach
                  </CardTitle>
                </div>
                {coachMatch?.compatibility_score && (
                  <Badge className="bg-terracotta text-white w-fit">
                    {coachMatch.compatibility_score}% Match
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Coach Photo */}
                  <div className="flex-shrink-0">
                    <div className="h-32 w-32 rounded-2xl overflow-hidden border-4 border-white shadow-lg mx-auto sm:mx-0">
                      <img 
                        src={matchedCoach.image_url} 
                        alt={matchedCoach.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=300'; }}
                      />
                    </div>
                  </div>
                  
                  {/* Coach Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-heading text-2xl font-bold text-forest mb-1">
                      {matchedCoach.name}
                    </h3>
                    <p className="text-terracotta font-medium mb-3">{matchedCoach.title}</p>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                      {matchedCoach.bio}
                    </p>
                    {matchedCoach.specialties && (
                      <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                        {matchedCoach.specialties.slice(0, 3).map((specialty, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-sand text-forest rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Match Rationale */}
                {coachMatch?.rationale && (
                  <div className="p-4 bg-white rounded-xl border border-sand">
                    <p className="text-sm font-medium text-forest mb-2 flex items-center gap-2">
                      <Star className="h-4 w-4 text-terracotta" />
                      Why {matchedCoach.name.split(' ')[0]} is Your Match
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {coachMatch.rationale}
                    </p>
                  </div>
                )}

                {/* Booking Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button 
                    onClick={handleBookChemistry}
                    className="flex-1 bg-forest hover:bg-forest/90 text-white"
                    data-testid="book-chemistry-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    15-Min Chemistry Session
                  </Button>
                  <Button 
                    onClick={handleBookSession}
                    variant="outline"
                    className="flex-1 border-terracotta text-terracotta hover:bg-terracotta/10"
                    data-testid="book-session-btn"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Book Inner Edge Session
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reflection Insights */}
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Heart className="h-5 w-5 text-terracotta" />
                Your Reflections
              </CardTitle>
              <CardDescription>
                What emerged from your honest self-assessment
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {clientView.reflection_insights?.map((insight, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-sand/30 rounded-xl">
                  <Quote className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">{insight}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Dominant Themes */}
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Target className="h-5 w-5 text-terracotta" />
                What&apos;s Emerging
              </CardTitle>
              <CardDescription>
                The themes that surfaced throughout your diagnostic
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {clientView.dominant_themes?.map((theme, i) => (
                  <span key={i} className="px-5 py-3 bg-forest text-white rounded-full text-sm font-medium">
                    {theme}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Focus Areas */}
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Star className="h-5 w-5 text-terracotta" />
                Your Growth Edges
              </CardTitle>
              <CardDescription>
                Where the coaching journey will focus
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {clientView.focus_areas?.map((area, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 border border-sand rounded-xl">
                    <span className="h-8 w-8 rounded-full bg-terracotta text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </span>
                    <p className="font-medium text-forest">{area}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Transformation Goal */}
          {clientView.transformation_goal && (
            <Card className="card-base mb-8 bg-gradient-to-br from-forest/5 to-terracotta/5">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">
                  Your Transformation Goal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-700 italic leading-relaxed">
                  &ldquo;{clientView.transformation_goal}&rdquo;
                </p>
              </CardContent>
            </Card>
          )}

          {/* Future Vision */}
          {clientView.future_vision && (
            <Card className="card-base mb-8 border-2 border-terracotta/20">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  Your Vision: 6 Months From Now
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {clientView.future_vision}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Encouragement & Invitation */}
          <Card className="card-base mb-8 bg-forest text-white">
            <CardContent className="p-8 text-center">
              <p className="text-lg mb-4 opacity-90">
                {clientView.encouragement || "You've shown remarkable courage in this reflection."}
              </p>
              <p className="text-xl font-heading font-semibold">
                {clientView.invitation || "This is where we'll begin together."}
              </p>
            </CardContent>
          </Card>

          {/* Recommended Videos Based on Results */}
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Video className="h-5 w-5 text-terracotta" />
                Recommended For You
              </CardTitle>
              <CardDescription>
                Videos from our library based on your assessment themes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <button 
                  onClick={() => navigate('/videos?category=Inner Edge Vault')}
                  className="group p-4 border border-sand rounded-xl hover:border-terracotta hover:bg-terracotta/5 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center group-hover:bg-terracotta/20">
                      <Video className="h-5 w-5 text-terracotta" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest">Understanding Your Results</p>
                      <p className="text-xs text-gray-500">Inner Edge Vault</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Deep dive into your Inner Edge assessment and what it means for your journey.</p>
                </button>

                <button 
                  onClick={() => navigate('/videos?category=Inner Circle Library')}
                  className="group p-4 border border-sand rounded-xl hover:border-terracotta hover:bg-terracotta/5 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center group-hover:bg-forest/20">
                      <Target className="h-5 w-5 text-forest" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest">Setting Your Intention</p>
                      <p className="text-xs text-gray-500">Inner Circle Library</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Define what leadership means to you and set clear intentions.</p>
                </button>

                {clientView.dominant_themes?.includes('Boundaries') && (
                  <button 
                    onClick={() => navigate('/videos?search=boundaries')}
                    className="group p-4 border border-sand rounded-xl hover:border-terracotta hover:bg-terracotta/5 transition-all text-left"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200">
                        <Shield className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-forest">The Power of Boundaries</p>
                        <p className="text-xs text-gray-500">Based on your themes</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Learn to protect your energy and time with healthy boundaries.</p>
                  </button>
                )}

                {clientView.dominant_themes?.some(t => t.toLowerCase().includes('leadership') || t.toLowerCase().includes('presence')) && (
                  <button 
                    onClick={() => navigate('/videos?search=leadership')}
                    className="group p-4 border border-sand rounded-xl hover:border-terracotta hover:bg-terracotta/5 transition-all text-left"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200">
                        <Crown className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-forest">Leadership Foundation</p>
                        <p className="text-xs text-gray-500">Based on your themes</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Establish your leadership foundation and presence.</p>
                  </button>
                )}
              </div>
              
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => navigate('/videos')} className="btn-outline">
                  Browse Full Library <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            {/* Only show retake if no coach has been assigned */}
            {!results?.coach_match?.coach && (
              <Button variant="outline" onClick={handleRetake} className="btn-outline">
                Retake Diagnostic
              </Button>
            )}
            <Button onClick={() => navigate('/sessions')} className="btn-primary">
              Book Your First Session <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Show Intro
  if (currentQuestionIndex === -1) {
    const intro = data.sections.find(s => s.id === 'intro');
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="diagnostic-intro">
          <div className="text-center mb-8">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-forest to-terracotta flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-forest">The Inner Edge™️ Diagnostic</h1>
            <p className="text-terracotta font-medium mt-2">A Self-Leadership Reset for Women Who Lead Boldly</p>
          </div>

          <Card className="card-base">
            <CardContent className="p-8">
              <h2 className="font-heading text-2xl text-forest mb-4">{intro?.title}</h2>
              <div className="text-gray-600 space-y-4 whitespace-pre-line leading-relaxed">
                {intro?.description}
              </div>
              
              <div className="mt-8 p-4 bg-sand/30 rounded-xl">
                <p className="text-sm text-gray-500 mb-2">This assessment includes:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-terracotta" />
                    <span>17 thoughtful questions across 5 sections</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-terracotta" />
                    <span>12-15 minutes of self-reflection</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-terracotta" />
                    <span>Personalized leadership insights</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={() => setCurrentQuestionIndex(0)} 
                className="btn-primary w-full mt-8"
              >
                Begin Your Journey <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Show Questionnaire
  const currentQ = data.questions[currentQuestionIndex];
  const currentSection = getCurrentSection();
  const progress = ((currentQuestionIndex + 1) / data.questions.length) * 100;

  // Check if we're starting a new section
  const isNewSection = currentQuestionIndex === 0 || 
    data.questions[currentQuestionIndex - 1]?.section !== currentQ?.section;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="diagnostic-page">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Question {currentQuestionIndex + 1} of {data.questions.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section Header (shown when starting a new section) */}
        {isNewSection && currentSection && (
          <div className="mb-6 p-4 bg-forest text-white rounded-xl">
            <h2 className="font-heading text-xl font-bold">{currentSection.title}</h2>
            <p className="text-white/80 mt-1">{currentSection.subtitle}</p>
          </div>
        )}

        {currentQ && (
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest leading-relaxed">
                {currentQ.question}
                {currentQ.required && <span className="text-terracotta ml-1">*</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Choice Question */}
              {currentQ.type === 'choice' && (
                <RadioGroup 
                  value={answers[currentQ.id] || ''} 
                  onValueChange={(value) => handleAnswer(currentQ.id, value)}
                  className="space-y-3"
                >
                  {currentQ.options?.map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <RadioGroupItem value={option} id={`option-${index}`} />
                      <Label 
                        htmlFor={`option-${index}`} 
                        className="flex-1 p-4 rounded-xl hover:bg-sand/50 cursor-pointer transition-colors border border-transparent hover:border-sand"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {/* Text Question */}
              {currentQ.type === 'text' && (
                <Textarea
                  value={answers[currentQ.id] || ''}
                  onChange={(e) => handleAnswer(currentQ.id, e.target.value)}
                  placeholder={currentQ.placeholder}
                  className="min-h-[150px] text-base"
                />
              )}

              {/* Scale Question */}
              {currentQ.type === 'scale' && (
                <div className="space-y-6 py-4">
                  <div className="px-4">
                    <Slider
                      value={[answers[currentQ.id] || currentQ.min]}
                      onValueChange={([value]) => handleAnswer(currentQ.id, value)}
                      min={currentQ.min}
                      max={currentQ.max}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 max-w-[100px]">{currentQ.min_label}</span>
                    <span className="text-3xl font-bold text-forest">
                      {answers[currentQ.id] || currentQ.min}
                    </span>
                    <span className="text-gray-500 max-w-[100px] text-right">{currentQ.max_label}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  className="btn-outline"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                
                {currentQuestionIndex < data.questions.length - 1 ? (
                  <Button onClick={handleNext} className="btn-primary">
                    Next <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    className="btn-secondary"
                    disabled={submitting}
                  >
                    {submitting ? 'Analyzing...' : 'Complete & Get Insights'} 
                    <Sparkles className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DiagnosticPage;
